import { ArrowRightIcon, AlertTriangleIcon, GitMergeIcon, XCircleIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import type { InterconnectPreview } from "@/hooks/use-interconnect"

interface InterconnectDialogProps {
  preview:   InterconnectPreview | null
  onConfirm: () => void
  onCancel:  () => void
}

function fmtPreviewDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, "0")
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function InterconnectDialog({ preview, onConfirm, onCancel }: InterconnectDialogProps) {
  const blocked = !!preview?.blockReason

  return (
    <Dialog
      open={!!preview}
      onOpenChange={(open) => { if (!open && !preview?.isLoading) onCancel() }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMergeIcon className="size-4" />
            Viaje de interconexión
          </DialogTitle>
          <DialogDescription>
            Se creará un viaje nuevo entre el destino del primer viaje y el origen del segundo.
          </DialogDescription>
        </DialogHeader>

        {preview && (() => {
          const { tripA, tripB, interconnectDate, isLoading, routeKm, blockReason } = preview
          const originName      = tripA.destinationCityName ?? "—"
          const destinationName = tripB.originCityName      ?? "—"
          const driverName      = tripB.driverName          ?? "—"
          const truck           = tripB.truckLicensePlate   ?? "—"
          const trailer         = tripB.trailerLicensePlate ?? "—"

          if (isLoading) {
            return (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                Calculando datos...
              </div>
            )
          }

          return (
            <div className="space-y-4 py-1">
              {/* Route summary — always shown so the user sees which trips are involved */}
              <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold flex-wrap">
                  <span className="text-muted-foreground">#{tripA.internalId}</span>
                  <span className="text-xs text-muted-foreground">destino</span>
                  <ArrowRightIcon className="size-3 shrink-0" />
                  <span>{originName}</span>
                  <ArrowRightIcon className="size-3 shrink-0" />
                  <span>{destinationName}</span>
                  <ArrowRightIcon className="size-3 shrink-0" />
                  <span className="text-xs text-muted-foreground">origen</span>
                  <span className="text-muted-foreground">#{tripB.internalId}</span>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <div className="text-muted-foreground">Chofer</div>
                  <div className="font-medium">{driverName}</div>

                  <div className="text-muted-foreground">Camión</div>
                  <div className="font-mono text-xs font-medium">{truck}</div>

                  <div className="text-muted-foreground">Semi</div>
                  <div className="font-mono text-xs font-medium">{trailer}</div>

                  {!blockReason && (
                    <>
                      <div className="text-muted-foreground">Fecha</div>
                      <div className="font-medium">
                        {interconnectDate
                          ? fmtPreviewDate(interconnectDate)
                          : <span className="text-muted-foreground italic">Sin fecha (null)</span>}
                      </div>

                      <div className="text-muted-foreground">Estado</div>
                      <div>
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">
                          Completado
                        </span>
                      </div>

                      <div className="text-muted-foreground">Carga</div>
                      <div className="text-muted-foreground italic text-xs">Sin carga asociada</div>
                    </>
                  )}
                </div>
              </div>

              {/* Block reason — replaces the normal route km / warning */}
              {blockReason ? (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                  <XCircleIcon className="size-4 shrink-0 mt-0.5" />
                  <span>{blockReason}</span>
                </div>
              ) : !routeKm ? (
                <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2.5 text-sm text-yellow-800">
                  <AlertTriangleIcon className="size-4 shrink-0 mt-0.5" />
                  <span>
                    No existe una ruta configurada entre{" "}
                    <strong>{originName}</strong> y <strong>{destinationName}</strong>.
                    El campo KM quedará vacío.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
                  <span className="font-medium">KM de ruta:</span>
                  <span>{routeKm} km</span>
                </div>
              )}
            </div>
          )
        })()}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={preview?.isLoading}
          >
            Cerrar
          </Button>
          {!blocked && (
            <Button
              onClick={onConfirm}
              disabled={preview?.isLoading}
            >
              {preview?.isLoading ? "Creando..." : "Crear viaje"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
